import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CalendarDays, Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import CtaButton from "@/components/CtaButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HorariosSemanal from "@/components/HorariosSemanal";
import { HorarioEditor, HORARIO_DEFECTO, HORARIO_FLEXIBLE_DEFECTO, horarioParaGuardar, resumenHorario } from "@/components/HorarioEditor";
import { HorarioModoConfig, modoConfigDefecto } from "@/components/HorarioModoConfig";
import {
  MODO_FIJO,
  MODO_FLEXIBLE,
  MODO_PATRON_TURNOS,
  mensajeObjetivoFlexible,
  modoConfigFromHorario,
  modoFromHorario,
  patronTurnosParaGuardar,
  resumenModoHorario,
} from "@/lib/horarioModos";

function horarioSemanalDeTurno(h) {
  if (h?.tramos?.[0]?.horario) return { ...HORARIO_DEFECTO, ...h.tramos[0].horario };
  if (h?.lun) return { ...HORARIO_DEFECTO, ...h };
  return { ...HORARIO_DEFECTO };
}

function horarioCatalogoParaGuardar(horarioSemanal, modoConfig) {
  const modo = modoConfig?.modo || MODO_FIJO;
  if (modo === MODO_PATRON_TURNOS) {
    return { modo, patron_turnos: patronTurnosParaGuardar(modoConfig.patron) };
  }
  if (modo === MODO_FLEXIBLE) {
    return {
      modo,
      flexible: { ...modoConfig.flexible },
      tramos: [{
        id: "base",
        nombre: "Horario",
        desde: "0001-01-01",
        hasta: "9999-12-31",
        horario: horarioParaGuardar(horarioSemanal),
      }],
    };
  }
  return { ...horarioParaGuardar(horarioSemanal), modo: MODO_FIJO };
}

function resumenTarjetaTurno(h) {
  const modo = modoFromHorario(h);
  if (modo === MODO_PATRON_TURNOS || modo === MODO_FLEXIBLE) return resumenModoHorario(h);
  return resumenHorario(horarioSemanalDeTurno(h));
}

function etiquetaModoTurno(h) {
  const modo = modoFromHorario(h);
  if (modo === MODO_PATRON_TURNOS) return "Rotativo";
  if (modo === MODO_FLEXIBLE) return "Libre";
  return "Fijo";
}

export default function Turnos() {
  const { user } = useAuth();
  const esAdmin = user.rol === "admin";
  const [turnos, setTurnos] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [festivos, setFestivos] = useState([]);
  const [centros, setCentros] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const anioActual = new Date().getFullYear();
  const [festivoYear, setFestivoYear] = useState(anioActual);
  const [festivoCentroId, setFestivoCentroId] = useState("");

  const [tab, setTab] = useState("semanal");
  const [semanalRefresh, setSemanalRefresh] = useState(0);
  const [turnoOpen, setTurnoOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [turnoForm, setTurnoForm] = useState({ nombre: "", color: "#18181b", horario: HORARIO_DEFECTO });
  const [turnoModo, setTurnoModo] = useState(modoConfigDefecto);

  const [asigOpen, setAsigOpen] = useState(false);
  const [asigForm, setAsigForm] = useState({ usuario_id: "", turno_id: "", fecha_inicio: hoyISO(), fecha_fin: "" });

  const [festivoOpen, setFestivoOpen] = useState(false);
  const [editFestivo, setEditFestivo] = useState(null);
  const [festivoForm, setFestivoForm] = useState({ fecha: hoyISO(), nombre: "", centro_id: "", tipo: "local" });
  const [guardandoFestivo, setGuardandoFestivo] = useState(false);
  const [cargandoFestivosOp, setCargandoFestivosOp] = useState(false);

  const cargarFestivos = useCallback(async (year, centroId) => {
    const params = { year };
    if (centroId) params.centro_id = Number(centroId);
    const { data } = await api.get("/festivos", { params });
    setFestivos(data);
  }, []);

  const cargar = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([
        api.get("/turnos"),
        api.get("/turnos/asignaciones"),
      ]);
      setTurnos(t.data);
      setAsignaciones(a.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    cargar();
    api.get("/usuarios").then((r) => setUsuarios(r.data.filter((u) => u.activo))).catch(() => {});
  }, [cargar]);

  useEffect(() => {
    if (user?.rol !== "admin" && user?.rol !== "manager") return;
    api.get("/centros").then((r) => {
      setCentros(r.data);
      setFestivoCentroId((prev) => prev || (r.data[0] ? String(r.data[0].id) : ""));
    }).catch(() => {});
  }, [user?.rol]);

  useEffect(() => {
    if (!festivoCentroId) return;
    cargarFestivos(festivoYear, festivoCentroId).catch((e) => toast.error(formatApiError(e)));
  }, [festivoYear, festivoCentroId, cargarFestivos]);

  const guardarTurno = async () => {
    const avisoObjetivo = mensajeObjetivoFlexible(turnoModo);
    if (avisoObjetivo && !window.confirm(avisoObjetivo)) return;
    try {
      if (editando) {
        await api.put(`/turnos/${editando.id}`, {
          ...turnoForm,
          horario: horarioCatalogoParaGuardar(turnoForm.horario, turnoModo),
        });
        toast.success("Turno actualizado");
      } else {
        await api.post("/turnos", {
          ...turnoForm,
          horario: horarioCatalogoParaGuardar(turnoForm.horario, turnoModo),
        });
        toast.success("Turno creado. Asígnalo en Vista semanal o Asignaciones para verlo en el calendario.");
      }
      setTurnoOpen(false);
      setTab("turnos");
      await cargar();
      setSemanalRefresh((n) => n + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const eliminarTurno = async (t) => {
    if (!window.confirm(`¿Eliminar el turno "${t.nombre}" y sus asignaciones?`)) return;
    try {
      await api.delete(`/turnos/${t.id}`);
      toast.success("Turno eliminado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const crearAsignacion = async () => {
    if (!asigForm.usuario_id || !asigForm.turno_id) {
      toast.error("Selecciona empleado y turno");
      return;
    }
    try {
      await api.post("/turnos/asignaciones", {
        usuario_id: Number(asigForm.usuario_id),
        turno_id: Number(asigForm.turno_id),
        fecha_inicio: asigForm.fecha_inicio,
        fecha_fin: asigForm.fecha_fin || null,
      });
      toast.success("Turno asignado");
      setAsigOpen(false);
      setAsigForm({ usuario_id: "", turno_id: "", fecha_inicio: hoyISO(), fecha_fin: "" });
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const eliminarAsignacion = async (a) => {
    if (!window.confirm("¿Eliminar esta asignación de turno?")) return;
    try {
      await api.delete(`/turnos/asignaciones/${a.id}`);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirFestivo = (f = null) => {
    setEditFestivo(f);
    setFestivoForm(f
      ? { fecha: f.fecha, nombre: f.nombre, centro_id: String(f.centro_id || festivoCentroId), tipo: f.tipo || "local" }
      : { fecha: `${festivoYear}-01-01`, nombre: "", centro_id: festivoCentroId, tipo: "local" });
    setFestivoOpen(true);
  };

  const guardarFestivo = async () => {
    if (guardandoFestivo) return;
    if (!festivoForm.nombre.trim()) {
      toast.error("Indica el nombre del festivo");
      return;
    }
    if (!festivoForm.centro_id) {
      toast.error("Selecciona un centro");
      return;
    }
    setGuardandoFestivo(true);
    try {
      const payload = {
        fecha: festivoForm.fecha,
        nombre: festivoForm.nombre.trim(),
        centro_id: Number(festivoForm.centro_id),
        tipo: festivoForm.tipo,
      };
      if (editFestivo) {
        await api.put(`/festivos/${editFestivo.id}`, payload);
        toast.success("Festivo actualizado");
      } else {
        await api.post("/festivos", payload);
        toast.success("Festivo añadido");
      }
      setFestivoOpen(false);
      setEditFestivo(null);
      await cargarFestivos(festivoYear, festivoCentroId);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoFestivo(false);
    }
  };

  const cargarNacionales = async () => {
    if (!festivoCentroId || cargandoFestivosOp) return;
    setCargandoFestivosOp(true);
    try {
      const { data } = await api.post("/festivos/seed-nacionales", null, {
        params: { year: festivoYear, centro_id: Number(festivoCentroId) },
      });
      toast.success(`${data.creados} festivos nacionales cargados`);
      await cargarFestivos(festivoYear, festivoCentroId);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargandoFestivosOp(false);
    }
  };

  const duplicarAnio = async () => {
    if (!festivoCentroId || cargandoFestivosOp) return;
    const destino = festivoYear + 1;
    if (!window.confirm(`¿Duplicar festivos de ${festivoYear} al ${destino}? Podrás editarlos después.`)) return;
    setCargandoFestivosOp(true);
    try {
      const { data } = await api.post("/festivos/duplicar", {
        origen_year: festivoYear,
        destino_year: destino,
        centro_id: Number(festivoCentroId),
      });
      toast.success(`${data.creados} festivos duplicados al ${destino}`);
      setFestivoYear(destino);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargandoFestivosOp(false);
    }
  };

  const eliminarFestivo = async (f) => {
    if (!window.confirm(`¿Eliminar el festivo "${f.nombre}"?`)) return;
    try {
      await api.delete(`/festivos/${f.id}`);
      toast.success("Festivo eliminado");
      cargarFestivos(festivoYear, festivoCentroId);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="turnos-page">
      <PageHeader
        icon={CalendarClock}
        title="Horarios y turnos"
        subtitle="Vista semanal del equipo, turnos rotativos y calendario festivo"
        sectionKey="turnos"
        brandGradient={false}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-lg bg-white shadow-sm">
          <TabsTrigger value="semanal" data-testid="tab-horarios-semanal">Vista semanal</TabsTrigger>
          <TabsTrigger value="turnos" data-testid="tab-turnos">Turnos</TabsTrigger>
          <TabsTrigger value="asignaciones" data-testid="tab-asignaciones">Asignaciones</TabsTrigger>
          <TabsTrigger value="festivos" data-testid="tab-festivos">Festivos</TabsTrigger>
        </TabsList>

        <TabsContent value="semanal" className="mt-4">
          <HorariosSemanal refreshKey={semanalRefresh} />
        </TabsContent>

        <TabsContent value="turnos" className="mt-4 space-y-4">
          {esAdmin && (
            <Button
              onClick={() => {
                setEditando(null);
                setTurnoModo(modoConfigDefecto());
                setTurnoForm({ nombre: "", color: "#18181b", horario: HORARIO_DEFECTO });
                setTurnoOpen(true);
              }}
              data-testid="crear-turno-btn"
              className="gap-1.5 rounded-lg bg-zinc-950 hover:bg-zinc-800"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Nuevo turno
            </Button>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="turnos-grid">
            {turnos.map((t) => (
              <div key={t.id} className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${t.color}1a` }}>
                    <CalendarClock className="h-4 w-4" style={{ color: t.color }} strokeWidth={1.5} />
                  </div>
                  {esAdmin && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setEditando(t);
                          setTurnoModo(modoConfigFromHorario(t.horario));
                          setTurnoForm({
                            nombre: t.nombre,
                            color: t.color,
                            horario: horarioSemanalDeTurno(t.horario),
                          });
                          setTurnoOpen(true);
                        }}
                        data-testid={`editar-turno-${t.id}`}
                        className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-950"
                      >
                        <Pencil className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => eliminarTurno(t)}
                        data-testid={`eliminar-turno-${t.id}`}
                        className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  )}
                </div>
                <h3 className="font-display mt-3 flex items-center gap-2 text-lg font-medium">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                  {t.nombre}
                </h3>
                <p className="text-sm text-zinc-500">{resumenTarjetaTurno(t.horario)}</p>
                <Badge variant="outline" className="mt-2 border-zinc-200 text-zinc-600">
                  {etiquetaModoTurno(t.horario)}
                </Badge>
                <p className="mt-2 text-sm text-zinc-400">
                  {t.num_asignados} asignado{t.num_asignados !== 1 ? "s" : ""}
                </p>
              </div>
            ))}
            {turnos.length === 0 && (
              <p className="col-span-full rounded-xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-400">
                Crea turnos (mañana, tarde, noche…) para horarios rotativos.
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="asignaciones" className="mt-4 space-y-4">
          {esAdmin && (
            <Button onClick={() => setAsigOpen(true)} data-testid="crear-asignacion-btn" className="gap-1.5 rounded-lg bg-zinc-950 hover:bg-zinc-800">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Asignar turno
            </Button>
          )}
          <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="asignaciones-tabla">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
                  <th className="px-4 py-3">Empleado</th>
                  <th className="px-4 py-3">Turno</th>
                  <th className="px-4 py-3">Desde</th>
                  <th className="px-4 py-3">Hasta</th>
                  {esAdmin && <th className="px-4 py-3 text-right"></th>}
                </tr>
              </thead>
              <tbody>
                {asignaciones.map((a) => (
                  <tr key={a.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                    <td className="px-4 py-2.5 font-medium">{a.usuario}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className="border-zinc-200" style={{ color: a.color }}>
                        {a.turno}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500">{fmtFecha(a.fecha_inicio)}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{a.fecha_fin ? fmtFecha(a.fecha_fin) : "Indefinido"}</td>
                    {esAdmin && (
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => eliminarAsignacion(a)}
                          data-testid={`eliminar-asignacion-${a.id}`}
                          className="rounded-md p-1.5 text-zinc-300 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {asignaciones.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-6 text-center text-zinc-400">
                      Sin asignaciones. Los empleados sin turno siguen el horario de su departamento.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="festivos" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-rose-100 bg-gradient-to-r from-orange-50 to-rose-50 p-4">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Centro de trabajo</Label>
              <select
                className="flex h-9 min-w-[180px] rounded-md border border-zinc-200 bg-white px-3 text-sm"
                value={festivoCentroId}
                onChange={(e) => setFestivoCentroId(e.target.value)}
                data-testid="festivo-centro-select"
              >
                {centros.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Año</Label>
              <select
                className="flex h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                value={festivoYear}
                onChange={(e) => setFestivoYear(Number(e.target.value))}
                data-testid="festivo-year-select"
              >
                {[anioActual - 1, anioActual, anioActual + 1, anioActual + 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {esAdmin && (
              <div className="flex flex-wrap gap-2 ml-auto">
                <CtaButton onClick={() => abrirFestivo()} data-testid="crear-festivo-btn" className="gap-1.5">
                  <Plus className="h-4 w-4" /> Añadir festivo
                </CtaButton>
                <Button variant="outline" onClick={cargarNacionales} disabled={cargandoFestivosOp} className="gap-1.5 border-rose-200">
                  <CalendarDays className="h-4 w-4" /> Nacionales España
                </Button>
                <Button variant="outline" onClick={duplicarAnio} disabled={cargandoFestivosOp} className="gap-1.5 border-rose-200">
                  <Copy className="h-4 w-4" /> Duplicar al año siguiente
                </Button>
              </div>
            )}
          </div>
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="festivos-tabla">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Festivo</th>
                  <th className="px-4 py-3">Tipo</th>
                  {esAdmin && <th className="px-4 py-3 text-right"></th>}
                </tr>
              </thead>
              <tbody>
                {festivos.map((f) => (
                  <tr key={f.id} className="border-b border-zinc-100 last:border-0 hover:bg-rose-50/40">
                    <td className="px-4 py-2.5 font-medium">{fmtFecha(f.fecha)}</td>
                    <td className="px-4 py-2.5">{f.nombre}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={f.tipo === "nacional" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-orange-200 bg-orange-50 text-orange-700"}>
                        {f.tipo === "nacional" ? "Nacional" : "Local"}
                      </Badge>
                    </td>
                    {esAdmin && (
                      <td className="px-4 py-2.5 text-right">
                        <button onClick={() => abrirFestivo(f)} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-950">
                          <Pencil className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <button onClick={() => eliminarFestivo(f)} data-testid={`eliminar-festivo-${f.id}`} className="rounded-md p-1.5 text-zinc-300 hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {festivos.length === 0 && (
                  <tr>
                    <td colSpan="4" className="p-6 text-center text-zinc-400">
                      Sin festivos este año para este centro. Carga los nacionales o añade festivos locales.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Diálogo turno */}
      <Dialog open={turnoOpen} onOpenChange={setTurnoOpen}>
        <DialogContent
          className="flex max-h-[min(90vh,860px)] max-w-3xl flex-col gap-0 overflow-hidden p-0"
          data-testid="turno-dialog"
        >
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-zinc-100 px-6 py-4 pr-12 text-left">
            <DialogTitle className="font-display">{editando ? "Editar turno" : "Nuevo turno"}</DialogTitle>
            <DialogDescription>
              Horario fijo de lunes a viernes, horario libre (flexible) o patrón rotativo con días de descanso.
              En el rotativo, cada turno puede repetir el mismo ciclo o usar otro (por ejemplo 5×2 y después 3×2).
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
            <div className="flex gap-3">
              <div className="flex-1 space-y-1.5">
                <Label>Nombre</Label>
                <Input
                  value={turnoForm.nombre}
                  onChange={(e) => setTurnoForm({ ...turnoForm, nombre: e.target.value })}
                  placeholder="Ej.: Rotativo mañana / tarde"
                  data-testid="turno-nombre-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Color</Label>
                <Input
                  type="color"
                  className="h-10 w-16 p-1"
                  value={turnoForm.color}
                  onChange={(e) => setTurnoForm({ ...turnoForm, color: e.target.value })}
                  data-testid="turno-color-input"
                />
              </div>
            </div>
            <HorarioModoConfig config={turnoModo} onChange={setTurnoModo} />
            {turnoModo.modo !== MODO_PATRON_TURNOS && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                  {turnoModo.modo === MODO_FLEXIBLE ? "Ventana de fichaje" : "Horario semanal"}
                </Label>
                {turnoModo.modo === MODO_FLEXIBLE && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setTurnoForm({ ...turnoForm, horario: HORARIO_FLEXIBLE_DEFECTO })}
                  >
                    Usar ventana libre lun–vie 06:00–22:00
                  </Button>
                )}
                <HorarioEditor
                  horario={turnoForm.horario}
                  onChange={(h) => setTurnoForm({ ...turnoForm, horario: h })}
                  prefix="turno-horario"
                  maxTurnosPorDia={turnoModo.modo === MODO_FLEXIBLE ? 1 : null}
                />
              </div>
            )}
          </div>
          <DialogFooter className="shrink-0 border-t border-zinc-100 bg-white px-6 py-3 sm:justify-end">
            <Button onClick={guardarTurno} disabled={!turnoForm.nombre.trim()} data-testid="turno-guardar-btn" className="bg-zinc-950 hover:bg-zinc-800">
              {editando ? "Guardar cambios" : "Crear turno"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo asignación */}
      <Dialog open={asigOpen} onOpenChange={setAsigOpen}>
        <DialogContent data-testid="asignacion-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Asignar turno</DialogTitle>
            <DialogDescription>El turno asignado sustituye al horario del departamento en ese periodo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Empleado</Label>
              <Select value={asigForm.usuario_id} onValueChange={(v) => setAsigForm({ ...asigForm, usuario_id: v })}>
                <SelectTrigger data-testid="asignacion-usuario-select">
                  <SelectValue placeholder="Selecciona empleado" />
                </SelectTrigger>
                <SelectContent>
                  {usuarios.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.nombre} {u.apellidos}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Turno</Label>
              <Select value={asigForm.turno_id} onValueChange={(v) => setAsigForm({ ...asigForm, turno_id: v })}>
                <SelectTrigger data-testid="asignacion-turno-select">
                  <SelectValue placeholder="Selecciona turno" />
                </SelectTrigger>
                <SelectContent>
                  {turnos.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Desde</Label>
                <Input
                  type="date"
                  value={asigForm.fecha_inicio}
                  onChange={(e) => setAsigForm({ ...asigForm, fecha_inicio: e.target.value })}
                  data-testid="asignacion-inicio-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Hasta (opcional)</Label>
                <Input
                  type="date"
                  value={asigForm.fecha_fin}
                  onChange={(e) => setAsigForm({ ...asigForm, fecha_fin: e.target.value })}
                  data-testid="asignacion-fin-input"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-400">
              Si el turno es rotativo o libre, una sola asignación aplica ese patrón en todo el periodo.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={crearAsignacion} data-testid="asignacion-guardar-btn" className="bg-zinc-950 hover:bg-zinc-800">
              Asignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo festivo */}
      <Dialog
        open={festivoOpen}
        onOpenChange={(open) => {
          if (!open && guardandoFestivo) return;
          setFestivoOpen(open);
          if (!open) setEditFestivo(null);
        }}
      >
        <DialogContent data-testid="festivo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editFestivo ? "Editar festivo" : "Añadir festivo"}</DialogTitle>
            <DialogDescription>
              Festivos del centro seleccionado. Los nacionales se cargan con la plantilla de España; puedes ajustar fechas variables.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Centro</Label>
              <select
                className="flex h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"
                value={festivoForm.centro_id}
                onChange={(e) => setFestivoForm({ ...festivoForm, centro_id: e.target.value })}
              >
                {centros.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Fecha</Label>
                <Input type="date" value={festivoForm.fecha} onChange={(e) => setFestivoForm({ ...festivoForm, fecha: e.target.value })} data-testid="festivo-fecha-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <select className="flex h-9 w-full rounded-md border border-zinc-200 px-3 text-sm" value={festivoForm.tipo} onChange={(e) => setFestivoForm({ ...festivoForm, tipo: e.target.value })}>
                  <option value="local">Local del centro</option>
                  <option value="nacional">Nacional</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={festivoForm.nombre} onChange={(e) => setFestivoForm({ ...festivoForm, nombre: e.target.value })} placeholder="Ej.: Día de la Constitución" data-testid="festivo-nombre-input" />
            </div>
          </div>
          <DialogFooter>
            <CtaButton
              type="button"
              onClick={guardarFestivo}
              disabled={!festivoForm.nombre.trim() || guardandoFestivo}
              data-testid="festivo-guardar-btn"
            >
              {guardandoFestivo ? "Guardando…" : editFestivo ? "Guardar" : "Añadir"}
            </CtaButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
